import { DownOutlined } from '@ant-design/icons';
import { Button, Dropdown, type MenuProps, Popover, Progress, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import clsx from 'clsx';
import { useMemo } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { FilesTableEnum } from '../../enums/FilesTableEnum';
import { QuickShareStatusEnum } from '../../enums/QuickShareStatusEnum';
import { useAuth } from '../../hooks/useAuth';
import { useFileUpload } from '../../hooks/useFileUpload';
import { useBranding } from '../../hooks/useBranding';
import useQuickShare from '../../hooks/useQuickShare';
import { getBrandingDataDefaultImage, getUserIcon } from '../../utils/utils';
import GlobalSearchField from '../Common/GlobalSearchField';
import QuickShareFilesList from '../Common/QuickShareFilesList';
import { Icon } from './Icon';

/**
 * Header component to render the top navigation bar for the application.
 *
 * This method manages multiple features such as branding logo, global search field,
 * quick share upload status, and user interaction elements like logout and navigation buttons.
 * The visibility and behavior of different parts of the header adapt dynamically based on
 * user authentication status, current route, and application state.
 *
 * @return {JSX.Element} The rendered header component for the application's top navigation bar.
 */
export default function Header() {
  const { authenticatedUser, logout, isLoggedIn, isReader, isExternal } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { fileId } = useParams<{ fileId: string }>();
  const { brandingData, isLoadingBrandingData } = useBranding();
  const { t } = useTranslation();
  const {
    uploadQuickShareGroupedFilesQueueWithoutRootFolder,
    isUploadingQuickShareFiles,
    uploadedQuickShareGroupedFilesAmount,
    totalQuickShareGroupedFilesAmount,
    isQuickShareUploadFilesCompletedWithoutErrors,
    quickShareGlobalProgressByBytes,
  } = useFileUpload();

  const { currentUploadStatus } = useQuickShare();

  const routesWithSearchField = ['/files', '/settings', '/settings/*', '/quick-share'];

  /**
   * A memoized boolean indicating whether the header should be visible.
   *
   * This value is derived from the application's current location pathname.
   * If the pathname contains '/file/', the header is not visible; otherwise, it is.
   *
   * Dependencies:
   * - location.pathname: The pathname from the current location object.
   */
  const isHeaderVisible = useMemo(() => {
    return !fileId;
  }, [location.pathname]);

  /**
   * A boolean indicating whether the search field should be visible based on the current authenticatedUser login status and URL path.
   *
   * This variable is computed using the useMemo hook, which recalculates its value only when the dependencies change.
   * Visibility is determined by the following conditions:
   * - The authenticatedUser must be logged in (isLoggedIn must be true).
   * - The current URL path (location.pathname) must match a set of predefined paths (urlWithSearchField) either exactly or as a base path.
   *
   * Dependencies:
   * - location.pathname: The current URL path.
   * - isLoggedIn: A boolean indicating whether the authenticatedUser is logged in.
   */
  const isSearchFieldVisible = useMemo(() => {
    if (!isLoggedIn) return false;

    return routesWithSearchField.some((route) => {
      if (route.startsWith('/files')) {
        return true;
      } else {
        return location.pathname === route;
      }
    });
  }, [location.pathname, isLoggedIn]);

  /**
   * Title representing the current state of a process based on its status.
   *
   * This memoized value determines the appropriate label to display depending on the
   * status of processes within `processStatuses`.
   * If any process is marked as `InProgress`, it returns the label for 'downloadPreparation';
   * otherwise, it returns the label for 'downloadReady'.
   *
   * Dependencies:
   * - processStatuses: Array of process objects to check their statuses.
   */
  const quickShareTitle: string = useMemo(() => {
    if (currentUploadStatus === QuickShareStatusEnum.ShareError) {
      return t('errors.filesNotShared');
    }

    // Check if there are no more files to upload ( No status uploading or pending )
    if (!isUploadingQuickShareFiles) {
      return t('common.uploadedFilesCounter', {
        amount: uploadedQuickShareGroupedFilesAmount,
        total: totalQuickShareGroupedFilesAmount,
      });
    } else {
      return t('common.uploadingFilesCounter', {
        amount: uploadedQuickShareGroupedFilesAmount,
        total: totalQuickShareGroupedFilesAmount,
      });
    }
  }, [
    isUploadingQuickShareFiles,
    uploadQuickShareGroupedFilesQueueWithoutRootFolder,
    totalQuickShareGroupedFilesAmount,
    uploadedQuickShareGroupedFilesAmount,
  ]);

  /**
   * A boolean variable indicating whether the current route matches the Quick Share route.
   * Determines if the application is currently on the "/quick-share" path.
   *
   * The value is memoized to optimize performance by recomputing only when the pathname
   * of the location changes.
   *
   * Dependencies:
   * - location.pathname
   */
  const isInQuickShareRoute = useMemo(() => {
    return location.pathname === '/quick-share';
  }, [location.pathname]);

  /**
   * Determines if the user is either an external user or a reader.
   *
   * This computed memoized value evaluates as true if either the `isExternal`
   * or `isReader` dependency evaluates to true. The value is recalculated
   * whenever the `isExternal` or `isReader` dependencies change.
   *
   * @type {boolean}
   */
  const isExternalOrReader = useMemo(() => {
    return isExternal || isReader;
  }, [isExternal, isReader]);

  /**
   * Returns an array of menu items for the user dropdown.
   *
   * This memoized function constructs an array of menu items based on the
   * authenticated user's role and the current location pathname.
   *
   * Dependencies:
   * - authenticatedUser: The authenticated user object.
   * - isExternalOrReader: A boolean indicating if the user is external or reader.
   * - location.pathname: The current location pathname.
   * - t: The translation function.
   * - navigate: The navigation function.
   * - logout: The logout function.
   *
   * @returns {MenuProps['items']} The array of menu items.
   */
  const menuItems = useMemo(() => {
    let items: MenuProps['items'] = [
      {
        key: 'profile',
        label: t(`roles.${authenticatedUser?.role}`),
        disabled: true,
      },
    ];

    if (!isExternalOrReader) {
      if (location.pathname === '/quick-share') {
        items.push({
          key: 'files',
          label: t('common.goToFileManager'),
          onClick: () => navigate(`/files/${FilesTableEnum.Personal}`),
        });
      } else {
        items.push({
          key: 'quick-share',
          label: t('common.goToQuickShare'),
          onClick: () => navigate('/quick-share'),
        });
      }
    }

    items.push({
      key: 'sign-out',
      label: t('common.signOut'),
      onClick: () => logout(),
    });

    return items;
  }, [authenticatedUser, isExternalOrReader, location.pathname, t, navigate, logout]);

  /**
   * Returns an icon representing the current status of a quick share upload.
   *
   * This memoized function takes the currentUploadStatus and returns an icon
   * based on the status. The icons are defined in the Icon component.
   *
   * @returns {ReactNode} The icon representing the current status of a quick share upload.
   */
  const quickShareStatusIcon = useMemo(() => {
    switch (currentUploadStatus) {
      case QuickShareStatusEnum.ConnectionError:
        return <Icon name="UnplugIcon" className="h-5 w-5 text-red-dark" />;
      case QuickShareStatusEnum.Error:
        return <Icon name="ServerCrashIcon" className="h-5 w-5 text-red-dark" />;
      case QuickShareStatusEnum.SomeErrors:
      case QuickShareStatusEnum.ShareError:
        return <Icon name="TriangleAlertIcon" className="h-5 w-5 text-yellow-dark" />;
      case QuickShareStatusEnum.Completed:
        return <Icon name="CircleCheckIcon" className="h-5 w-5 text-green" />;
      default:
        return '';
    }
  }, [currentUploadStatus]);

  return (
    <header
      className={clsx(
        'fixed left-0 right-0 top-0 z-40 h-14 w-[100vw]',
        isLoadingBrandingData ? 'animate-pulse bg-gray-medium' : 'bg-blue-dark',
        !isHeaderVisible && 'pointer-events-none opacity-0',
      )}
      style={{ backgroundColor: brandingData?.topBarColorBackground }}
    >
      <div className="flex h-full items-center justify-between px-4">
        <div className="flex flex-row items-center gap-6">
          <Link to={`/files/${FilesTableEnum.Personal}`}>
            <div className="lg:w-48">
              <img
                src={
                  brandingData?.topBarLogo
                    ? `${brandingData.topBarLogo}?v=${Date.now()}`
                    : getBrandingDataDefaultImage('top-bar')
                }
                alt={brandingData?.companyName || 'Filegrant Enterprise'}
                className={clsx(
                  'h-8 transition-all duration-300',
                  isLoadingBrandingData && 'opacity-25 blur-lg',
                )}
              />
            </div>
          </Link>
          {isSearchFieldVisible && (
            <div className="hidden sm:block">
              <GlobalSearchField />
            </div>
          )}
        </div>
        {isLoggedIn && (
          <div className="flex flex-row items-center gap-3">
            {!isExternalOrReader && (
              <>
                {uploadQuickShareGroupedFilesQueueWithoutRootFolder.length > 0 && (
                  <Popover
                    placement="bottomRight"
                    content={
                      <div className="bg-white p-0">
                        <QuickShareFilesList
                          files={uploadQuickShareGroupedFilesQueueWithoutRootFolder}
                          handleRemoveFile={() => {}}
                        />
                        {isQuickShareUploadFilesCompletedWithoutErrors && !isInQuickShareRoute && (
                          <div className="z-10 flex w-full justify-center pb-2 text-gray-dark underline">
                            <Link to="/quick-share">{t('common.startNewUploadAndShare')}</Link>
                          </div>
                        )}
                      </div>
                    }
                  >
                    <Button
                      onClick={() => navigate('/quick-share')}
                      className="bg-white text-white"
                    >
                      <div className="flex items-center justify-between gap-8">
                        <div className="text-sm font-medium text-blue-dark">{quickShareTitle}</div>
                        <div className="flex items-center gap-1">
                          {quickShareStatusIcon}
                          <Progress
                            type="circle"
                            percent={quickShareGlobalProgressByBytes}
                            size={16}
                            showInfo={false}
                          />
                          <span className="text-sm text-blue-dark">
                            {quickShareGlobalProgressByBytes}%
                          </span>
                          <DownOutlined className="ml-2 h-4 w-4 text-gray-dark" />
                        </div>
                      </div>
                    </Button>
                  </Popover>
                )}
              </>
            )}
            <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
              <Button className="bg-white px-2 py-1 text-white">
                <div className="flex flex-row items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-medium uppercase text-blue-dark">
                    {authenticatedUser?.firstName?.[0] ||
                      authenticatedUser?.lastName?.[0] ||
                      authenticatedUser?.userName?.[0] ||
                      authenticatedUser?.email?.[0] ||
                      'N/D'}
                  </div>
                  <div className="flex flex-col items-start">
                    <div className="flex flex-row items-center gap-1">
                      {authenticatedUser?.role && (
                        <Icon
                          name={getUserIcon(authenticatedUser)}
                          className="h-4 w-4 text-blue-dark"
                        />
                      )}
                      <Tooltip title={authenticatedUser?.email || authenticatedUser?.userName}>
                        <span className="text-sm text-blue-dark">
                          {authenticatedUser?.firstName || authenticatedUser?.lastName ? (
                            <>
                              {authenticatedUser?.firstName} {authenticatedUser?.lastName}
                            </>
                          ) : (
                            <>{authenticatedUser?.email || authenticatedUser?.userName}</>
                          )}
                        </span>
                      </Tooltip>
                      <DownOutlined className="ml-2 h-4 w-4 text-gray-dark" />
                    </div>
                  </div>
                </div>
              </Button>
            </Dropdown>
          </div>
        )}
      </div>
    </header>
  );
}
